import { Fragment } from 'react';

/** Above this many allowed runs, slots become a bar: twenty pips read as texture. */
const MAX_SLOTS = 10;
/** Waiting runs drawn one by one before the rest collapse into "+N". */
const MAX_WAITING = 5;

export interface CapacitySlotsProps {
  running: number;
  /** The enforced limit (already clamped by the ceiling). */
  limit: number;
  queued: number;
  size?: 'sm' | 'md';
  /**
   * Hidden from assistive technology, for when the control around it already names
   * the figures (spec 118: the capacity meter), so they are not read twice.
   */
  decorative?: boolean;
}

/**
 * A workspace's run slots, drawn (spec 117 FR-016): filled for running, empty for
 * free, dashed past the limit mark for runs waiting their turn. One element, so the
 * Limits table and the board's capacity meter show capacity the same way.
 *
 * Decorative to the eye and a sentence to assistive technology: `role="img"` with the
 * figures as its name, and every slot hidden from the accessibility tree.
 */
export function CapacitySlots({
  running,
  limit,
  queued,
  size = 'sm',
  decorative = false,
}: CapacitySlotsProps) {
  const label =
    queued > 0
      ? `${String(running)} of ${String(limit)} running, ${String(queued)} waiting`
      : `${String(running)} of ${String(limit)} running`;
  const slot =
    size === 'sm' ? 'h-3 w-1.5 rounded-[2px]' : 'h-3.5 w-2 rounded-[3px]';
  const shownWaiting = Math.min(queued, MAX_WAITING);
  const moreWaiting = queued - shownWaiting;
  // Running can exceed the limit once an administrator lowers it — lowering never
  // stops a run — and a running run is never hidden (spec 118 R2). The mark then sits
  // where the limit is, inside the filled slots.
  const drawn = Math.max(limit, running);
  const markInside = running > limit;
  const limitMark = (
    <span
      aria-hidden="true"
      data-slot="limit"
      className="bg-foreground/35 mx-0.5 h-4 w-px rounded-full"
    />
  );

  return (
    <span
      {...(decorative
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': label })}
      className="inline-flex shrink-0 items-center gap-[3px]"
    >
      {drawn <= MAX_SLOTS ? (
        Array.from({ length: drawn }, (_, index) => (
          <Fragment key={index}>
            {markInside && index === limit ? limitMark : null}
            <span
              aria-hidden="true"
              data-slot={index < running ? 'running' : 'free'}
              className={`${slot} ${index < running ? 'bg-primary' : 'bg-border'}`}
            />
          </Fragment>
        ))
      ) : (
        <span
          aria-hidden="true"
          data-slot="bar"
          className={`bg-border relative overflow-hidden rounded-full ${size === 'sm' ? 'h-1.5 w-16' : 'h-2 w-24'}`}
        >
          <span
            className="bg-primary absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${String(Math.min(100, (running / limit) * 100))}%`,
            }}
          />
        </span>
      )}
      {queued > 0 ? (
        <>
          {markInside && drawn <= MAX_SLOTS ? null : limitMark}
          {Array.from({ length: shownWaiting }, (_, index) => (
            <span
              key={`w${String(index)}`}
              aria-hidden="true"
              data-slot="waiting"
              className={`${slot} border-primary/55 border border-dashed`}
            />
          ))}
          {moreWaiting > 0 ? (
            <span
              aria-hidden="true"
              className="text-primary ml-0.5 text-xs font-medium tabular-nums"
            >
              +{String(moreWaiting)}
            </span>
          ) : null}
        </>
      ) : null}
    </span>
  );
}
